<template>
  <b-modal  v-model="isOpen"
            size="lg"
            centered
            scrollable
            @shown="handleShown"
            @hide="handleClose">

    <template #title>
      <h2 class="modal-title">KPI-Instanz hinzufügen</h2>
    </template>

    <template #default>
      <BOverlay :show="!isClientReady || !isReady" variant="dark" opacity="0.5">
        <template #overlay>
          <div class="text-center">
            <BSpinner variant="light" class="mb-2" />
            <p class="text-white mb-0">{{ $t('Webportal.Dashboard.Loading') }}...</p>
          </div>
        </template>
        <p class="modal-description">
          Wählen Sie eine KPI, konfigurieren Sie die Filter (Land, Welle, Unter-KPI) und fügen Sie sie hinzu
        </p>

        <KpiSearchBar :items="kpiSearchOptions"
                      v-model="kpiSearchQuery"
                      placeholder="Verfügbare KPIs durchsuchen..."
                      @select="onKpiSelected"
                      class="mb-3" />

        <!-- TODO: kpi details for one kpi (but full width)... using this.kpi
            like it is done in add-kpi-content...available-kpis-section for multiple items (same layout) -->

        <div class="add-kpi-content">
          <!-- Left: Available KPIs -->
          <div class="available-kpis-section">
            <div class="section-header">
              <h3 class="section-title">Verfügbare KPIs</h3>
              <p class="section-subtitle">
                Wählen Sie eine KPI und Filter, dann klicken Sie auf "Hinzufügen"
              </p>
            </div>

            <div class="kpi-categories-scroll">
              <div class="kpi-categories">
                <KpiGroupDetails  v-for="group in kpiOptionsProp"
                                  :key="group.id"
                                  :group="group">
                  <KpiDetails v-for="kpi in group.children"
                              :key="kpi.id"
                              :kpi="kpi"
                              :country-options="countryOptions"
                              :wave-options="waveOptions"
                              @add="addKpi" />
                </KpiGroupDetails>
              </div>
            </div>
          </div>

          <!-- Right: Added KPIs -->
          <div class="added-kpis-section">
            <div class="section-header">
              <div>
                <h3 class="section-title">Hinzugefügte KPI-Instanzen</h3>
                <p class="section-subtitle">{{ addedKpis.length }} Instanzen aktiv</p>
              </div>
            </div>

            <div class="added-kpis-scroll">
              <div class="added-kpis-list">
                <div
                  v-for="(kpi, index) in addedKpis"
                  :key="`added-${index}`"
                  class="added-kpi-item"
                >
                  <div class="added-kpi-header">
                    <div class="added-kpi-info">
                      <p class="added-kpi-name">{{ kpi.name }}</p>
                      <p class="added-kpi-category">{{ kpi.group }}</p>
                    </div>
                    <b-button
                      variant="ghost"
                      size="sm"
                      class="remove-button"
                      @click="removeKpi(index)"
                    >
                      <X :size="16" />
                    </b-button>
                  </div>

                  <div class="added-kpi-badges">
                    <span class="kpi-badge">
                      <Globe :size="12" class="me-1" />
                      {{ kpi.country }}
                    </span>
                    <span class="kpi-badge">
                      <Activity :size="12" class="me-1" />
                      {{ kpi.wave }}
                    </span>
                    <span class="kpi-badge kpi-badge-muted">
                      {{ kpi.status }}
                    </span>
                    <span class="kpi-badge">
                      <Layers :size="12" class="me-1" />
                      {{ kpi.subKpi }}
                    </span>
                  </div>

                  <div class="added-kpi-values">
                    <span class="value-item">
                      Wert: <strong class="value-current">{{ kpi.value }}%</strong>
                    </span>
                    <span class="value-separator">•</span>
                    <span class="value-item">
                      Ziel: <strong>{{ kpi.target }}%</strong>
                    </span>
                    <span class="value-separator">•</span>
                    <span class="value-trend">{{ formatTrend(kpi.trend) }}</span>
                  </div>
                </div>

                <div v-if="addedKpis.length === 0" class="empty-state">
                  <p class="empty-state-text">
                    Noch keine KPIs hinzugefügt. Wählen Sie eine KPI aus der Liste links aus.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </BOverlay>
    </template>

    <template #footer="{ cancel }">
      <b-button variant="outline-secondary" @click="cancel()">
        Schließen
      </b-button>
    </template>
  </b-modal>

</template>

<script>
import { Globe, Activity, Layers, Plus, X } from 'lucide-vue-next';
import KpiSearchBar from '../KpiSearchBar.vue';
import KpiGroupDetails from './KpiGroupDetails.vue';
import KpiDetails from './KpiDetails.vue';
import SurveysService from '@/services/SurveysService';

import { mapGetters } from 'vuex';

export default {
  name: 'KpiAddDialog',

  components: {
    Globe,
    Activity,
    Layers,
    Plus,
    X,
    KpiSearchBar,
    KpiGroupDetails,
    KpiDetails
  },

  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:modelValue', 'kpis-added'],

  data() {
    return {
      addedKpis: [],
      countryOptions: [],
      waveOptions: [],

      kpi: null,
      kpiOptions: [],
      kpiSearchOptions: [],
      kpiSearchQuery: '',

      locks: {
        waves:false,
        kpi:false
      }
    };
  },

  async mounted() {
    if (this.isClientReady) {
      await this.init();
    }
  },

  computed: {
    ...mapGetters('auth', [ 'clientId' ]),
    ...mapGetters('localization', [ 'currentLocale' ]),
    ...mapGetters('clientData', [ 'isClientReady', 'project', 'surveyIds', 'countryCodes' ]),

    isOpen: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },

    kpiOptionsProp() {
      console.warn(this.kpiSearchQuery );
      const items = this.kpiOptions.filter(k => k.label.includes(this.kpiSearchQuery) || k.children.filter(c => c.label.includes(this.kpiSearchQuery)) || this.kpiSearchQuery === '');
      console.warn(items.length);
      if (items.length > 0) {
        this.isReady = true;
      }
      return items;
    },

    isReady() {
      return this.isClientReady && this.kpiOptions.length > 0
          && this.waveOptions && this.waveOptions.length > 0
          && this.countryOptions && this.countryOptions.length > 0;
    }
  },

  methods: {
    async init() {
      this.waveOptions = await this.LoadWaves();
      console.warn("I N I ");
      const options = await this.LoadKpiOptions();
      this.kpiOptions = this.getKpiOptions(options);
      this.kpiSearchOptions = this.getKpiSearchOptions(options);
      this.countryOptions = this.countryCodes.map(code => ({ id: code, label: code }));
    },

    getKpiOptions(kpis) {
      if (!kpis || kpis.length === 0)
        return [];
      let results = [];

try {
      kpis.forEach(survey => {
        survey.children.forEach(tranche => {
          if (!tranche.children) {
            console.warn("Tranche with no children:", survey);
            // debugger
          } else {
            console.warn(tranche.children);
            tranche.children.forEach(group => {
              const g = group;
              let activeGroup = results.find(r => r.label === g.label);
              if (!activeGroup) {
                activeGroup = { label: g.label, group: g.label, type: 'group', children: [] };
                results.push(activeGroup);
              }
              g.children.forEach(kpi => {
                if (!activeGroup.children.find(r => r.label === kpi.label)) {
                  activeGroup.children.push({
                    label: kpi.label,
                    id: kpi.kpiId,
                    group: g.label,
                    selectedWave: null,
                    selectedCountry: null,
                    type: 'kpi'
                  });
                }
              });
            });
          }
        });
      });
      }
catch(e) {
  console.error("Error in getKpiOptions:", e);
}
      return _.orderBy(results || [], ['label'], ['asc']);
    },

    getKpiSearchOptions(kpis) {
      if (!kpis || kpis.length === 0)
        return [];
      let results = [];
try {
      kpis.forEach(survey => {
        survey.children.forEach(tranche => {
          tranche.children.forEach(group => {
            const g = group;
            if (!results.find(r => r.label === g.label)) {
              results.push({ label: g.label, group: g.label, type: 'group' });
            }
            group.children.forEach(kpi => {
              if (!results.find(r => r.label === g.label + ' => ' + kpi.label)) {
                results.push({
                  label: g.label + ' => ' + kpi.label,
                  group: g.label,
                  kpi: kpi.label,
                  type: 'kpi'
                });
              }
            });
          });
        });
      });
      }
catch(e) {
  console.error("Error in getKpiSearchOptions:", e);
}
      return _.orderBy(results || [], ['type', 'label'], ['asc', 'asc']);
    },

    async LoadKpiOptions() {
      if (this.locks.kpi)
        return this.kpiOptions;
      this.locks.kpi = true;

      const kpiResponse = await SurveysService.getSurveyKpisWithIds(
        { surveyIds: this.surveyIds }, this.currentLocale);
      return kpiResponse.data || [];
    },

    async LoadWaves() {
      if (this.locks.waves)
        return this.waveOptions;
      this.locks.waves = true;

      const waveFilter = {
        clientIds: this.clientId,
        projectIds: this.project ? this.project.ClientProject.ClientProjectId : null,
        surveyIds: this.surveyIds,
      };

      const waveResponse = await SurveysService.getSurveyWaves(
        waveFilter, this.currentLocale);

      let waveResults = [];
      try {
      waveResponse.data.forEach(survey => {
        survey.children.forEach(w => {
          const wave = waveResults.find(s => s.label === w.label);
          if (wave) {
            wave.surveyIds.push(w.id);
          }
          else {
            waveResults.push( { id: w.label, label: w.label, surveyIds:[w.id], SortOrder: w.SortOrder } );
          }
        });
      });
      }
      catch(e) {
        console.error("Error in LoadWaves:", e);
      }
      return _.orderBy(waveResults || [], ['SortOrder'], ['desc']);
    },

    async onKpiSelected(selectedKpi) {
      console.warn('Selected KPI from search:', selectedKpi);
    },

    getSubKpiOptions(kpi) {
      return kpi.subKpis.map(sub => ({ value: sub, text: sub }));
    },

    addKpi(kpi) {
      const newKpi = {
        id: Date.now(),
        name: `${kpi.label} - ${kpi.selectedSubKpi}`,
        category: kpi.group,
        country: kpi.selectedCountry,
        wave: kpi.selectedWave,
        status: 'Datafreeze',
        subKpi: kpi.selectedSubKpi,
        value: Math.floor(Math.random() * 20) + 80,
        target: 95,
        trend: Math.floor(Math.random() * 10) - 5,
        previousValue: Math.floor(Math.random() * 20) + 80
      };

      this.addedKpis.push(newKpi);
    },

    removeKpi(index) {
      this.addedKpis.splice(index, 1);
    },

    formatTrend(trend) {
      if (trend === 0) return '0pp';
      const sign = trend > 0 ? '+' : '';
      return `${sign}${trend}pp`;
    },

    handleClose() {
      if (this.addedKpis.length > 0) {
        console.warn("EMIT kpis-added", this.addedKpis.length);
        this.$emit('kpis-added', [...this.addedKpis]);
        this.addedKpis = [];
      }
      this.isOpen = false;
    },
    async handleShown() {

      if(this.isReady) {
        return;
      }
      console.warn("I N I T Client Ready:", this.isClientReady, " FALSE MEANS FAIL");
      console.warn("I N I T Ready:", this.isReady, " FALSE MEANS FAIL");
      await this.init();
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--bmw-primary);
  margin: 0;
}

.modal-description {
  font-size: 14px;
  color: var(--color-muted-foreground);
  margin-bottom: 0rem;
}

.add-kpi-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  height: 600px;
}

.available-kpis-section,
.added-kpis-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.available-kpis-section {
  border-right: 1px solid var(--color-border);
  padding-right: 16px;
}

.added-kpis-section {
  padding-left: 16px;
}

.section-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.section-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-foreground);
  margin: 0;
}

.section-subtitle {
  font-size: 12px;
  color: var(--color-muted-foreground);
  margin: 0;
}

.kpi-categories-scroll,
.added-kpis-scroll {
  flex: 1;
  overflow-y: auto;
  padding-right: 4px;
}

.kpi-categories {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.added-kpis-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.added-kpi-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: white;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  transition: all 0.15s;

  &:hover {
    border-color: rgba(var(--bmw-primary-rgb), 0.3);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
}

.added-kpi-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.added-kpi-info {
  flex: 1;
  min-width: 0;
}

.added-kpi-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-foreground);
  margin: 0 0 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.added-kpi-category {
  font-size: 12px;
  color: var(--color-muted-foreground);
  margin: 0;
}

.remove-button {
  padding: 2px;
  color: #9ca3af;

  &:hover {
    color: var(--default-status-red);
    background: #fee2e2;
  }
}

.added-kpi-badges {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.kpi-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 500;
  background: #f9fafb;
  color: #374151;
  border: 1px solid var(--color-border);
  border-radius: 4px;

  &-muted {
    color: var(--color-muted-foreground);
    border-color: #d1d5db;
  }
}

.added-kpi-values {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--color-muted-foreground);
  flex-wrap: wrap;
}

.value-item {
  white-space: nowrap;
}

.value-current {
  color: var(--bmw-primary);
}

.value-separator {
  color: #d1d5db;
}

.value-trend {
  font-weight: 500;
  color: var(--color-muted-foreground);
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  padding: 24px;
  text-align: center;
}

.empty-state-text {
  font-size: 14px;
  color: #9ca3af;
  margin: 0;
}

// Dark Mode
:root.dark,
[data-bs-theme="dark"] {
  .modal-title,
  .section-title,
  .kpi-item-name,
  .added-kpi-name {
    color: var(--color-foreground);
  }

  .modal-description,
  .section-subtitle,
  .kpi-item-description,
  .added-kpi-category,
  .added-kpi-values,
  .empty-state-text {
    color: var(--color-muted-foreground);
  }

  .available-kpis-section {
    border-right-color: var(--color-border);
  }

  .kpi-item,
  .added-kpi-item {
    background: var(--color-card);
    border-color: var(--color-border);
  }

  .kpi-badge {
    background: var(--color-muted);
    color: var(--color-foreground);
    border-color: var(--color-border);
  }

  .value-current {
    color: var(--color-primary);
  }
}
</style>
